import {
  DeductionType,
  EarningType,
  LeavePolicy,
  LeaveType,
  PayrollSettings,
  StaffCategory,
  StatutoryRuleSet,
} from '../types/hr'

export const defaultDepartments: {
  name: string
  category: StaffCategory
}[] = [
  { name: 'Medical / Clinical', category: 'Doctors' },
  { name: 'Nursing', category: 'Nurses' },
  { name: 'Pharmacy', category: 'Allied Health' },
  { name: 'Laboratory', category: 'Allied Health' },
  { name: 'Radiology', category: 'Allied Health' },
  { name: 'Maternity', category: 'Nurses' },
  { name: 'Reception', category: 'Administrative' },
  { name: 'Administration', category: 'Administrative' },
  { name: 'Accounts', category: 'Administrative' },
  { name: 'Housekeeping', category: 'Support' },
  { name: 'Security', category: 'Support' },
  { name: 'Maintenance', category: 'Support' },
]

/** Default positions, keyed by department name. */
export const defaultPositions: { title: string; department: string }[] = [
  { title: 'Medical Officer', department: 'Medical / Clinical' },
  { title: 'Clinical Officer', department: 'Medical / Clinical' },
  { title: 'Registered Nurse', department: 'Nursing' },
  { title: 'Enrolled Nurse', department: 'Nursing' },
  { title: 'Registered Midwife', department: 'Maternity' },
  { title: 'Pharmacist', department: 'Pharmacy' },
  { title: 'Pharmacy Technologist', department: 'Pharmacy' },
  { title: 'Biomedical Scientist', department: 'Laboratory' },
  { title: 'Radiographer', department: 'Radiology' },
  { title: 'Receptionist', department: 'Reception' },
  { title: 'Hospital Administrator', department: 'Administration' },
  { title: 'Accountant', department: 'Accounts' },
  { title: 'Cleaner', department: 'Housekeeping' },
  { title: 'Security Guard', department: 'Security' },
  { title: 'Maintenance Technician', department: 'Maintenance' },
]

export const employmentTypes = [
  'Permanent',
  'Fixed-term contract',
  'Part-time',
  'Casual',
  'Locum',
  'Other',
] as const

export const staffCategories: StaffCategory[] = [
  'Doctors',
  'Nurses',
  'Allied Health',
  'Administrative',
  'Support',
]

/** System earning ids used by the payroll engine for attendance-driven pay. */
export const SYSTEM_EARNINGS = {
  basic: 'basic',
  nightDuty: 'night-duty',
  weekendDuty: 'weekend-duty',
  onCall: 'on-call',
  publicHoliday: 'public-holiday',
  overtime: 'overtime',
  unpaidLeave: 'unpaid-leave',
} as const

export const defaultEarningTypes: EarningType[] = [
  { id: 'basic', name: 'Basic Salary', taxable: true, pensionable: true, system: true },
  { id: 'housing', name: 'Housing Allowance', taxable: true, pensionable: true },
  { id: 'transport', name: 'Transport Allowance', taxable: true, pensionable: true },
  { id: 'lunch', name: 'Lunch Allowance', taxable: true, pensionable: true },
  { id: 'medical', name: 'Medical Allowance', taxable: true, pensionable: true },
  { id: 'night-duty', name: 'Night Duty Allowance', taxable: true, pensionable: true, system: true },
  { id: 'on-call', name: 'On-Call Allowance', taxable: true, pensionable: true, system: true },
  { id: 'overtime', name: 'Overtime', taxable: true, pensionable: true, system: true },
  { id: 'public-holiday', name: 'Public Holiday Pay', taxable: true, pensionable: true, system: true },
  { id: 'weekend-duty', name: 'Weekend Duty Allowance', taxable: true, pensionable: true, system: true },
  { id: 'professional', name: 'Professional Allowance', taxable: true, pensionable: true },
  { id: 'responsibility', name: 'Responsibility Allowance', taxable: true, pensionable: true },
  { id: 'acting', name: 'Acting Allowance', taxable: true, pensionable: true },
  { id: 'locum', name: 'Locum Payment', taxable: true, pensionable: true },
  { id: 'bonus', name: 'Bonus', taxable: true, pensionable: true },
  { id: 'unpaid-leave', name: 'Unpaid Leave / Absence', taxable: true, pensionable: true, system: true },
  { id: 'other-earning', name: 'Other Earnings', taxable: true, pensionable: true },
]

export const defaultDeductionTypes: DeductionType[] = [
  { id: 'pension', name: 'Pension Contribution', preTax: false },
  { id: 'union', name: 'Union Subscription', preTax: false },
  { id: 'recovery', name: 'Authorised Recovery', preTax: false },
  { id: 'other-deduction', name: 'Other Deductions', preTax: false },
]

export const defaultLeaveTypes: LeaveType[] = [
  { id: 'annual', name: 'Annual Leave', entitlementDays: 24, paid: true, accruesMonthly: true },
  { id: 'sick', name: 'Sick Leave', entitlementDays: 30, paid: true, accruesMonthly: false },
  { id: 'maternity', name: 'Maternity Leave', entitlementDays: 98, paid: true, accruesMonthly: false },
  { id: 'paternity', name: 'Paternity Leave', entitlementDays: 5, paid: true, accruesMonthly: false },
  { id: 'compassionate', name: 'Compassionate Leave', entitlementDays: 12, paid: true, accruesMonthly: false },
  { id: 'unpaid', name: 'Unpaid Leave', entitlementDays: 30, paid: false, accruesMonthly: false },
]

/**
 * Starting statutory figures (Zambia, 2025). These MUST be checked against the
 * current ZRA, NAPSA and NHIMA circulars and updated in Payroll Settings by
 * adding a new effective-dated rule set — they are not hard-coded anywhere.
 */
export const defaultStatutoryRuleSet: StatutoryRuleSet = {
  id: 'rules-2025-01',
  label: 'Zambia 2025 (verify with ZRA / NAPSA / NHIMA)',
  effectiveFrom: '2025-01',
  payeBands: [
    { upTo: 5100, rate: 0 },
    { upTo: 7100, rate: 20 },
    { upTo: 9200, rate: 30 },
    { upTo: null, rate: 37 },
  ],
  napsaEmployeeRate: 5,
  napsaEmployerRate: 5,
  napsaMonthlyCeiling: 34164,
  nhimaEmployeeRate: 1,
  nhimaEmployerRate: 1,
  nhimaBasis: 'basic',
  sdlRate: 0.5,
  workersCompRate: 0,
  createdAt: '2025-01-01T00:00:00.000Z',
  createdByName: 'System default',
}

/** 2 days per completed month of service (24 per year). Casual and locum staff do not accrue by default. */
export const defaultLeavePolicy: LeavePolicy = {
  leaveTypeId: 'annual',
  daysPerMonth: 2,
  maxCarryForwardDays: 0,
  eligibleEmploymentTypes: ['Permanent', 'Fixed-term contract', 'Part-time', 'Other'],
}

export const defaultPayrollSettings: PayrollSettings = {
  paymentDay: 25,
  workingDaysPerMonth: 22,
  hoursPerDay: 8,
  overtimeMultiplier: 1.5,
  publicHolidayMultiplier: 2,
  nightDutyRate: 150,
  weekendDutyRate: 120,
  onCallRate: 100,
  deductAbsences: true,
  netPayRounding: 'none',
  payslipPrefix: 'HMS-PS',
  employeePrefix: 'HMS-EMP',
  earningTypes: defaultEarningTypes,
  deductionTypes: defaultDeductionTypes,
  leaveTypes: defaultLeaveTypes,
  leavePolicy: defaultLeavePolicy,
  salaryGrades: [],
  statutoryRuleSets: [defaultStatutoryRuleSet],
}

export const hrDocumentCategories = [
  'Employment contract',
  'Identification',
  'Professional certificate',
  'Academic certificate',
  'Appointment letter',
  'Promotion letter',
  'Disciplinary record',
  'Other',
] as const

export const shiftTypes = [
  'Day',
  'Night',
  'Weekend',
  'Public Holiday',
  'On-call',
  'Overtime',
  'Absent',
  'Late',
] as const
